// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState, useMemo } from 'react';
import { InquieryCreateBody } from 'presentations/api/schema/event';
import Email from 'domains/models/user/email';
import { ViewerItem } from 'presentations/api/schema/user';
import { ErrorList } from 'domains/models';
import { eventTypeThemeColors } from 'domains/models/event_type/color';
import { relativeLuminanceW3C } from 'presentations/views/helpers/color';
import { apiPath } from 'presentations/routes/path';
import { post } from 'infras/rest_client';
import { FormPageItem } from 'presentations/api/schema/form';

interface Props {
  form: FormPageItem;
  viewer: ViewerItem | null;
}

export const useFormFooter = (props: Props) => {
  const t = translateInComponent(translations);

  const [isShowingInquityForm, setIsShowingInquityForm] = useState<boolean>(
    false,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(
    props.viewer ? props.viewer.email : null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(
    props.viewer ? props.viewer.name : null,
  );
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [errors, setErrors] = useState<ErrorList>({});

  const validateInquiryForm = (): boolean => {
    setIsDisabled(true);
    const newErrors: ErrorList = {
      ...validate('email', email),
      ...validate('name', name),
      ...validate('company', company),
      ...validate('message', message),
    };
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return false;
    }
    setIsDisabled(false);
    return true;
  };

  const onChange = (e: any) => {
    setIsDisabled(false);
    const { name, value } = e.currentTarget;
    const updateValue = value;
    switch (name) {
      case 'email':
        setEmail(updateValue);
        break;
      case 'message':
        setMessage(updateValue);
        break;
      case 'name':
        setName(updateValue);
        break;
      case 'company':
        setCompany(updateValue);
        break;
      case 'phoneNumber':
        setPhoneNumber(updateValue);
        break;
      default:
        break;
    }
    const newErrors = validate(name, updateValue);
    if (Object.keys(newErrors).length > 0) {
      setIsDisabled(true);
    }
    setErrors(newErrors);
  };

  const onClickInquiry = () => {
    setIsShowingInquityForm(true);
  };

  const onCloseInquiry = () => {
    setIsShowingInquityForm(false);
  };

  const onSubmitInquiry = async () => {
    if (!validateInquiryForm()) {
      setIsLoading(true);
      // 少しローディングさせてから戻す
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return;
    }
    if (!name || !email || !message) return;

    const data: InquieryCreateBody = {
      name: name,
      email: email,
      message: message,
      company,
      projectId: props.form.projectId,
    };
    setIsLoading(true);
    const result = await post(apiPath(`/v1/forms/${props.form.uid}/inquiry`), {
      data,
    });
    if (!result.isSuccess) {
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    return result;
  };

  const validate = (name: string, value: any): ErrorList => {
    const newErrors: ErrorList = {
      ...errors,
    };
    switch (name) {
      case 'email':
        const emailErrors: string[] = [];
        if (!value) {
          // emailErrors.push('model.EventContact.email.E001');
        }
        const newEmail = new Email(value);
        if (!newEmail.isValid()) {
          emailErrors.push('model.EventContact.email.E002');
        }
        newErrors['email'] = emailErrors;
        if (emailErrors.length === 0) {
          delete newErrors['email'];
        }
        break;
      case 'message':
        const messageErrors: string[] = [];
        if (isShowingInquityForm) {
          if (!value) {
            messageErrors.push('model.EventContact.message.E002');
          }
        }
        newErrors['message'] = messageErrors;
        if (messageErrors.length === 0) {
          delete newErrors['message'];
        }
        break;
      case 'name':
        const nameErrors: string[] = [];
        if (!value) {
          nameErrors.push('model.EventContact.name.E001');
        }
        newErrors['name'] = nameErrors;
        if (nameErrors.length === 0) {
          delete newErrors['name'];
        }
        break;
      case 'company':
        const companyErrors: string[] = [];
        newErrors['company'] = companyErrors;
        if (companyErrors.length === 0) {
          delete newErrors['company'];
        }
        break;
      case 'phoneNumber':
        const phoneNumberErrors: string[] = [];
        newErrors['phoneNumber'] = phoneNumberErrors;
        if (phoneNumberErrors.length === 0) {
          delete newErrors['phoneNumber'];
        }
        break;
      default:
    }
    return newErrors;
  };

  const onBlur = (e: any) => {
    const { name, value } = e.currentTarget;
    const newErrors = validate(name, value);
    setErrors(newErrors);
  };

  const isLightColor = useMemo(
    () =>
      !eventTypeThemeColors.includes(props.form.color) &&
      relativeLuminanceW3C(props.form.color) >= 0.5,
    [props.form.color],
  );

  return {
    state: {
      isDisabled,
      isLoading,
      isShowingInquityForm,
      isLightColor,
      email,
      name,
      message,
      company,
      phoneNumber,
      errors,
    },
    actions: {
      onClickInquiry,
      onCloseInquiry,
      onSubmitInquiry,
      onChange,
      onBlur,
    },
  };
};
